import { formatApiUrl } from "@/utils/Common";
import { Request } from "./Request";
import { showError } from "@/utils/UI";
import router from "@/router";

import stringFormat from "string-format";
import trimEnd from "lodash/trimEnd";

import store from "../stores";
import { Method } from "axios";

export declare type DuduApiConfig = { url: string; path: { [key: string]: any } };
export declare type DuduApi = string | { url: string; path?: { [key: string]: any } };

let ajaxRequestCounter = 0;
let isStart = false;
let token: any;

function completeProcess() {
  isStart = false;
  clearTimeout(token);
  token = window.setTimeout(() => {
    if (!isStart) {
      store.commit("isLoading", false);
    }
  }, 200);
}

function startProcess() {
  if (isStart) {
    return;
  }
  isStart = true;
  store.commit("isLoading", true);
}

export class Ajax {
  private _request: Request;

  constructor(request: Request) {
    this._request = request;
  }

  async get<ReturnType>(
    api: DuduApi,
    params: Nullable<{ [key: string]: any }> = {},
    showLoading = true,
    errorShow = true
  ) {
    const queryStrs = Object.assign({}, params);
    const querys: Map<string, string> = new Map<string, string>();

    Object.keys(queryStrs).forEach((prop) => querys.set(prop, queryStrs[prop]));

    let url = "";
    if (typeof api === "string") {
      url = stringFormat(api, params);
    } else {
      url = stringFormat(api.url, api.path);
    }

    if (querys.size > 0) {
      const strs = [];
      for (const [key, value] of querys.entries()) {
        strs.push(`${key}=${encodeURIComponent(value)}`);
      }
      if (url.indexOf("?") > -1) {
        url += "&" + strs.join("&");
      } else {
        url += "?" + strs.join("&");
      }
    }

    return await this.queryData<ReturnType>(url, params, "GET", showLoading, errorShow);
  }

  async post<ReturnType>(api: DuduApi, params: Nullable<{ [key: string]: any }>, showLoading = true, errorShow = true) {
    return await this.queryData<ReturnType>(api, params, "POST", showLoading, errorShow);
  }

  async put<ReturnType>(api: DuduApi, params: Nullable<{ [key: string]: any }>) {
    return await this.queryData<ReturnType>(api, params, "PUT");
  }

  async delete<ReturnType>(api: DuduApi, id: string) {
    let url = "";
    if (typeof api === "string") {
      url = stringFormat(api, { id });
    } else {
      url = stringFormat(api.url, api.path);
    }
    const strs = [`id=${id}`];
    if (url.indexOf("?") > -1) {
      url += "&" + strs.join("&");
    } else {
      url += "?" + strs.join("&");
    }
    return await this.queryData<ReturnType>(url, { id }, "DELETE");
  }

  async queryData<T>(
    api: DuduApi,
    params: Nullable<{ [key: string]: any }> = {},
    method: Method,
    showLoading = true,
    errorShow = true
  ): Promise<T> {
    try {
      if (params === null || params === undefined) {
        params = {};
      }
      if (showLoading) {
        Ajax.showLoading();
      }
      if (typeof api === "string") {
        api = trimEnd(stringFormat(api, params), "/");
      } else {
        api = trimEnd(stringFormat(api.url, api.path), "/");
      }

      const url = formatApiUrl(api, { dev: 0 });

      const res = await this._request.request<T>(url, params, method);
      if (showLoading) {
        Ajax.hideLoading();
      }
      return res.data;
    } catch (e: any) {
      if (showLoading) {
        Ajax.hideLoading();
      }

      if (errorShow) {
        if (e.response.status === 401) {
          await showError("授权状态已失效，请重新登录");
          await router.replace({ name: "login" });
        } else {
          if (e.response.data && e.response.data.message) {
            showError(e.response.data.message, true);
          }
        }
        // showError(`错误码:${e.response.status}<br/>原因:${e.response.data.message}<br/>接口: ${api}<br/>参数: ${JSON.stringify(params)}<br/>时间: ${new Date().toISOString()}`, true);
      }
      throw e;
    }
  }

  private static hideLoading() {
    ajaxRequestCounter -= 1;
    if (ajaxRequestCounter === 0) {
      completeProcess();
    }
  }

  private static showLoading() {
    ajaxRequestCounter += 1;
    startProcess();
  }
}

export default new Ajax(new Request());
